import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { Session } from '@supabase/supabase-js';
import { config } from '../lib/config';
import {
  MATCH_COPY_AUDIENCES,
  MATCH_COPY_DEFAULTS,
  MATCH_COPY_FIELDS,
  MATCH_COPY_SPORT_EDITOR_OPTIONS,
  MATCH_COPY_STEPS,
  MATCH_COPY_STYLE_EDITOR_OPTIONS,
  MATCH_COPY_VARIANT_EDITOR_OPTIONS,
  PREVIEW_SAMPLE_VARS,
  cloneValue,
  countOverridesForFields,
  deletePathValue,
  fieldsForAudience,
  fieldsForStep,
  formatTemplate,
  getEffectiveMatchCopy,
  getContextualPathValue,
  getPathValue,
  isObject,
  normalizeMatchCopyOverrides,
  resolveScopedFieldPath,
  setPathValue,
  shortUserLabel,
  stableStringify,
  textValue,
  type MatchCopyAudienceKey,
  type MatchCopyEditorScope,
  type MatchCopyField,
  type MatchCopyHistoryItem,
  type MatchCopyPreviewContext,
  type MatchCopyStepKey,
} from './schema';

type StatusTone = 'info' | 'success' | 'error' | 'warning';

type StatusMessage = {
  tone: StatusTone;
  text: string;
};

type AdminConsoleResponse<T> = { ok?: boolean; error?: string; value?: unknown; settings?: unknown } & T;

type DraftMeta = {
  updated_at: string | null;
  updated_by: string | null;
};

type SaveState = 'saved' | 'saving' | 'unsaved' | 'error';
type EditorMode = 'content' | 'preview' | 'history';
type PreviewRole = 'host' | 'player';
type PreviewViewport = 'phone' | 'tablet' | 'desktop';
type MatchCopyIntent = 'titles' | 'guidance' | 'actions' | 'labels' | 'rich';
type PreviewTarget = 'base' | 'style' | 'sport' | 'variant';

type ScenarioKey =
  | 'host_waiting'
  | 'host_locked'
  | 'host_ready'
  | 'player_join'
  | 'player_get_next'
  | 'player_joining'
  | 'player_join_session'
  | 'host_gate'
  | 'player_gate'
  | 'result_buttons'
  | 'what_next'
  | 'host_bonus'
  | 'player_bonus'
  | 'recap'
  | 'close_only'
  | 'stepper'
  | 'points_sheet'
  | 'actions'
  | 'game_on';

type MatchCopyGetResponse = {
  value?: Record<string, unknown>;
  settings?: { updated_at?: string | null; updated_by?: string | null } | null;
  history?: MatchCopyHistoryItem[];
};

type MatchCopySaveResponse = MatchCopyGetResponse;

const MATCH_COPY_KEY = 'match_experience_copy_v1';

const AUDIENCE_META: Record<
  MatchCopyAudienceKey,
  { title: string; description: string; emphasis: string }
> = {
  host: {
    title: 'Host surface',
    description: 'Controller-only titles, prompts, and guidance that drive the round forward.',
    emphasis: 'Use this lane for host-facing labels and round-management copy.',
  },
  players: {
    title: 'Player surface',
    description: 'Join, get-next, and waiting-state copy shown to participants.',
    emphasis: 'Good for join prompts, queue messaging, and player-side state updates.',
  },
  shared: {
    title: 'Shared labels',
    description: 'Stepper labels, headings, and copy that both sides see.',
    emphasis: 'Best for match-wide labels that should stay consistent across the whole flow.',
  },
  actions: {
    title: 'Action labels',
    description: 'Buttons and loading states used across the match flow.',
    emphasis: 'Change verbs and CTA tone here without touching long-form guidance.',
  },
  sheets: {
    title: 'Sheets & explainers',
    description: 'Long-form body copy and info surfaces like the points explainer.',
    emphasis: 'Use this lane for richer copy blocks and HTML-backed content.',
  },
};

const STEP_SCENARIOS: Record<
  MatchCopyStepKey,
  Array<{ key: ScenarioKey; label: string; role?: PreviewRole }>
> = {
  lobby: [
    { key: 'host_waiting', label: 'Host waiting', role: 'host' },
    { key: 'host_locked', label: 'Teams locked', role: 'host' },
    { key: 'host_ready', label: 'Ready to start', role: 'host' },
    { key: 'player_join', label: 'Join', role: 'player' },
    { key: 'player_get_next', label: 'Get next', role: 'player' },
    { key: 'player_joining', label: 'Already joining', role: 'player' },
    { key: 'player_join_session', label: 'Join session', role: 'player' },
  ],
  game_on: [{ key: 'game_on', label: 'Live round' }],
  round_complete: [
    { key: 'host_gate', label: 'Host gate', role: 'host' },
    { key: 'player_gate', label: 'Player waiting', role: 'player' },
    { key: 'result_buttons', label: 'Result buttons', role: 'host' },
  ],
  series_decision: [{ key: 'what_next', label: "What's next", role: 'host' }],
  loser_challenge: [
    { key: 'host_bonus', label: 'Host decision', role: 'host' },
    { key: 'player_bonus', label: 'Players waiting', role: 'player' },
  ],
  post_match: [
    { key: 'recap', label: 'Recap footer' },
    { key: 'close_only', label: 'Close only', role: 'player' },
  ],
  global: [
    { key: 'stepper', label: 'Stepper' },
    { key: 'points_sheet', label: 'Points sheet' },
    { key: 'actions', label: 'Action labels' },
  ],
};

const INTENT_META: Array<{ key: MatchCopyIntent; label: string; help: string }> = [
  { key: 'titles', label: 'Titles', help: 'Headings and primary section copy.' },
  { key: 'guidance', label: 'Guidance', help: 'Instructions, helper text, and host/player messaging.' },
  { key: 'actions', label: 'CTAs', help: 'Buttons, next actions, and close/accept verbs.' },
  { key: 'labels', label: 'Labels', help: 'Stepper labels, short names, and compact UI text.' },
  { key: 'rich', label: 'Rich text', help: 'Long-form explainers and sheet content.' },
];

const DEFAULT_EDITOR_SCOPE: MatchCopyEditorScope = { kind: 'base' };
const DEFAULT_PREVIEW_CONTEXT: MatchCopyPreviewContext = {
  copyStyle: 'game',
  sportId: 'pickleball',
  variant: 'high_score',
  activityLabel: 'Bench Press',
};

function scopeSummary(scope: MatchCopyEditorScope) {
  switch (scope.kind) {
    case 'style':
      return `Family override · ${scope.copyStyle}`;
    case 'sport':
      return `Sport override · ${scope.sportId}`;
    case 'variant':
      return `Variant override · ${scope.sportId} / ${scope.variant}`;
    case 'base':
    default:
      return 'Base copy';
  }
}

function fieldIntent(field: MatchCopyField): MatchCopyIntent {
  const haystack = `${field.label} ${field.path} ${field.help ?? ''}`.toLowerCase();
  if (field.kind === 'rich_text' || haystack.includes('html') || haystack.includes('sheet')) return 'rich';
  if (haystack.includes('cta') || haystack.includes('button') || haystack.includes('accept') || haystack.includes('decline') || haystack.includes('close') || haystack.includes('join game')) return 'actions';
  if (haystack.includes('subtitle') || haystack.includes('guidance') || haystack.includes('hint') || haystack.includes('body')) return 'guidance';
  if (haystack.includes('title') || haystack.includes('heading')) return 'titles';
  return 'labels';
}

function sortFieldsForEditing(fields: MatchCopyField[]) {
  const audienceOrder: Record<MatchCopyAudienceKey, number> = {
    host: 0,
    shared: 1,
    actions: 2,
    players: 3,
    sheets: 4,
  };
  const intentOrder: Record<MatchCopyIntent, number> = {
    titles: 0,
    guidance: 1,
    actions: 2,
    labels: 3,
    rich: 4,
  };
  return [...fields].sort((a, b) => {
    const audienceDelta = audienceOrder[a.audience] - audienceOrder[b.audience];
    if (audienceDelta !== 0) return audienceDelta;
    const intentDelta = intentOrder[fieldIntent(a)] - intentOrder[fieldIntent(b)];
    if (intentDelta !== 0) return intentDelta;
    return a.label.localeCompare(b.label);
  });
}

function summarizeField(field: MatchCopyField) {
  const label = field.label.toLowerCase();
  if (label.includes('subtitle')) return 'Subtitle';
  if (label.includes('title')) return 'Title';
  if (label.includes('guidance')) return 'Guidance';
  if (label.includes('cta')) return 'CTA';
  if (label.includes('hint')) return 'Hint';
  if (label.includes('label')) return 'Label';
  if (label.includes('heading')) return 'Heading';
  return field.label;
}

function formatDate(ts: string | null | undefined) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

async function adminFetch<T>(
  session: Session,
  payload: Record<string, unknown>,
): Promise<AdminConsoleResponse<T>> {
  const url = `${config.supabaseUrl}/functions/v1/admin-console`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: config.supabaseAnonKey,
      authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  });
  const body = (await response.json().catch(() => ({}))) as AdminConsoleResponse<T>;
  if (!response.ok || body?.error) {
    throw new Error(body?.error || `Request failed (${response.status})`);
  }
  return body;
}

function statusPill(status: StatusMessage) {
  return <div className={clsx('admin-status', status.tone)}>{status.text}</div>;
}

function shouldShowStatusBanner(status: StatusMessage | null) {
  return Boolean(status && status.tone !== 'success');
}

function MatchCopyRichEditor({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder: string;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== value) ref.current.innerHTML = value || '';
  }, [value]);

  const exec = useCallback((command: string, arg?: string) => {
    try {
      document.execCommand(command, false, arg);
    } catch {
      // ignore
    }
    ref.current?.focus();
  }, []);

  return (
    <div className="cms-rich">
      <div className="cms-rich-toolbar">
        <button className="admin-btn small" type="button" onClick={() => exec('bold')}>Bold</button>
        <button className="admin-btn small" type="button" onClick={() => exec('italic')}>Italic</button>
        <button className="admin-btn small" type="button" onClick={() => exec('insertUnorderedList')}>Bullets</button>
        <button className="admin-btn small" type="button" onClick={() => exec('formatBlock', 'h3')}>H3</button>
        <button className="admin-btn small" type="button" onClick={() => exec('formatBlock', 'p')}>P</button>
        <button
          className="admin-btn small"
          type="button"
          onClick={() => {
            const url = window.prompt('Link URL');
            if (url) exec('createLink', url);
          }}
        >
          Link
        </button>
      </div>
      <div
        ref={ref}
        className="cms-rich-editor"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={() => onChange(ref.current?.innerHTML ?? '')}
      />
      <div className="admin-help">
        Use headings, paragraphs, bullets, and links. Tokens like <span className="admin-mono">{'{winPoints}'}</span> are supported.
      </div>
    </div>
  );
}

function MatchCopyPreview({
  step,
  scenario,
  role,
  viewport,
  effective,
  previewStateDetail,
  setRole,
  setViewport,
  setScenario,
}: {
  step: MatchCopyStepKey;
  scenario: ScenarioKey;
  role: PreviewRole;
  viewport: PreviewViewport;
  effective: Record<string, unknown>;
  previewStateDetail: string;
  setRole: (next: PreviewRole) => void;
  setViewport: (next: PreviewViewport) => void;
  setScenario: (next: ScenarioKey) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const scenarioOptions = STEP_SCENARIOS[step].filter((item) => !item.role || item.role === role);
  const scenarioExists = scenarioOptions.some((item) => item.key === scenario);
  const activeScenario = scenarioExists ? scenario : scenarioOptions[0]?.key ?? STEP_SCENARIOS[step][0].key;
  const previewOrigin = useMemo(() => {
    if (typeof window === 'undefined') return 'https://play.rvlryapp.com';
    const params = new URLSearchParams(window.location.search);
    const overridden = params.get('playPreviewOrigin');
    if (overridden) return overridden.replace(/\/$/, '');
    return 'https://play.rvlryapp.com';
  }, []);
  const previewUrl = useMemo(() => {
    const query = new URLSearchParams({
      step,
      scenario: activeScenario,
      role,
      viewport,
    });
    return `${previewOrigin}/preview/match-flow?${query.toString()}`;
  }, [activeScenario, previewOrigin, role, step, viewport]);

  const pushPreviewState = useCallback(() => {
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    target.postMessage(
      {
        source: 'rvlry-admin-match-copy',
        type: 'match-copy-preview:update',
        payload: {
          copySettings: effective,
          step,
          scenario: activeScenario,
          role,
          viewport,
        },
      },
      previewOrigin,
    );
  }, [activeScenario, effective, previewOrigin, role, step, viewport]);

  useEffect(() => {
    setIframeLoaded(false);
    setIframeError(null);
  }, [previewUrl]);

  useEffect(() => {
    if (!iframeLoaded) return;
    const timer = window.setTimeout(() => {
      pushPreviewState();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [iframeLoaded, pushPreviewState]);

  return (
    <section className="cms-preview-pane">
      <div className="cms-preview-pane-head">
        <div>
          <div className="admin-section-title">Preview canvas</div>
          <div className="cms-preview-status">
            {iframeError
              ? 'Preview failed to load. Retry or open it in a new tab.'
              : iframeLoaded
                ? previewStateDetail
                : 'Loading real play preview…'}
          </div>
        </div>
        <div className="cms-stage-header-actions">
          <button className={clsx('admin-btn small', role === 'host' && 'primary')} type="button" onClick={() => setRole('host')}>Host</button>
          <button className={clsx('admin-btn small', role === 'player' && 'primary')} type="button" onClick={() => setRole('player')}>Player</button>
          <button className={clsx('admin-btn small', viewport === 'phone' && 'primary')} type="button" onClick={() => setViewport('phone')}>Phone</button>
          <button className={clsx('admin-btn small', viewport === 'tablet' && 'primary')} type="button" onClick={() => setViewport('tablet')}>Tablet</button>
          <button className={clsx('admin-btn small', viewport === 'desktop' && 'primary')} type="button" onClick={() => setViewport('desktop')}>Desktop</button>
          <button className="admin-btn small" type="button" onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}>
            Open in new tab
          </button>
          <button
            className="admin-btn small"
            type="button"
            onClick={() => {
              setIframeLoaded(false);
              setIframeError(null);
              if (iframeRef.current) iframeRef.current.src = previewUrl;
            }}
          >
            Refresh preview
          </button>
        </div>
      </div>
      <div className="cms-stage">
        <div className="cms-stage-toolbar">
          <div className="cms-stage-toolbar-group">
            {scenarioOptions.map((item) => (
              <button
                key={item.key}
                className={clsx('admin-btn small', activeScenario === item.key && 'primary')}
                type="button"
                onClick={() => setScenario(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className="cms-stage-canvas">
          <div className={clsx('match-copy-preview-frame', 'iframe-shell', viewport)}>
            {!iframeLoaded ? <div className="cms-preview-overlay">Loading preview…</div> : null}
            {iframeError ? <div className="cms-preview-overlay error">{iframeError}</div> : null}
            <iframe
              ref={iframeRef}
              title="Real match flow preview"
              src={previewUrl}
              className="cms-match-preview-iframe"
              onLoad={() => {
                setIframeLoaded(true);
                setIframeError(null);
                window.setTimeout(() => pushPreviewState(), 40);
              }}
              onError={() => {
                setIframeLoaded(false);
                setIframeError('Unable to load the play preview route.');
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function MatchCopyWorkspace({ session }: { session: Session }) {
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [meta, setMeta] = useState<DraftMeta>({ updated_at: null, updated_by: null });
  const [history, setHistory] = useState<MatchCopyHistoryItem[]>([]);
  const [baseline, setBaseline] = useState<Record<string, unknown>>({});
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [previewOverrides, setPreviewOverrides] = useState<Record<string, unknown>>({});
  const [editorMode, setEditorMode] = useState<EditorMode>('content');
  const [step, setStep] = useState<MatchCopyStepKey>('lobby');
  const [audienceFilter, setAudienceFilter] = useState<MatchCopyAudienceKey | 'all'>('all');
  const [intentFilter, setIntentFilter] = useState<MatchCopyIntent | 'all'>('all');
  const [editorScope, setEditorScope] = useState<MatchCopyEditorScope>(DEFAULT_EDITOR_SCOPE);
  const [selectedFieldPath, setSelectedFieldPath] = useState<string>('');
  const [search, setSearch] = useState('');
  const [modifiedOnly, setModifiedOnly] = useState(false);
  const [previewRole, setPreviewRole] = useState<PreviewRole>('host');
  const [previewViewport, setPreviewViewport] = useState<PreviewViewport>('phone');
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget>('base');
  const [previewContext, setPreviewContext] = useState<MatchCopyPreviewContext>(DEFAULT_PREVIEW_CONTEXT);
  const [scenarioByStep, setScenarioByStep] = useState<Record<MatchCopyStepKey, ScenarioKey>>({
    lobby: 'host_waiting',
    game_on: 'game_on',
    round_complete: 'host_gate',
    series_decision: 'what_next',
    loser_challenge: 'host_bonus',
    post_match: 'recap',
    global: 'stepper',
  });
  const [sampleVars, setSampleVars] = useState<Record<string, string | number>>({
    host: PREVIEW_SAMPLE_VARS.host,
    controller: PREVIEW_SAMPLE_VARS.controller,
    winPoints: PREVIEW_SAMPLE_VARS.winPoints,
    copyStyle: PREVIEW_SAMPLE_VARS.copyStyle,
    sportId: PREVIEW_SAMPLE_VARS.sportId,
    variant: PREVIEW_SAMPLE_VARS.variant,
    activityLabel: PREVIEW_SAMPLE_VARS.activityLabel,
  });

  const draftRef = useRef(draft);
  const baselineRef = useRef(baseline);
  const metaRef = useRef(meta);
  const savePromiseRef = useRef<Promise<boolean> | null>(null);
  const pendingAutosaveRef = useRef(false);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    baselineRef.current = baseline;
  }, [baseline]);

  useEffect(() => {
    metaRef.current = meta;
  }, [meta]);

  const dirty = useMemo(
    () => stableStringify(normalizeMatchCopyOverrides(draft)) !== stableStringify(baseline),
    [baseline, draft],
  );

  useEffect(() => {
    if (saveState === 'saving' || saveError) return;
    setSaveState(dirty ? 'unsaved' : 'saved');
  }, [dirty, saveError, saveState]);

  const effectiveDraft = useMemo(() => getEffectiveMatchCopy(draft, previewContext), [draft, previewContext]);
  const effectivePreview = useMemo(() => {
    const context =
      previewTarget === 'base'
        ? undefined
        : previewTarget === 'style'
          ? { ...previewContext, sportId: null, variant: null }
          : previewTarget === 'sport'
            ? { ...previewContext, variant: null }
            : previewContext;
    return getEffectiveMatchCopy(previewOverrides, context);
  }, [previewContext, previewOverrides, previewTarget]);
  const currentScenario = scenarioByStep[step];
  const editorVariantOptions = useMemo(
    () => (editorScope.kind === 'variant' ? MATCH_COPY_VARIANT_EDITOR_OPTIONS[editorScope.sportId] ?? [] : []),
    [editorScope]
  );
  const previewVariantOptions = useMemo(
    () => (previewContext.sportId ? MATCH_COPY_VARIANT_EDITOR_OPTIONS[previewContext.sportId] ?? [] : []),
    [previewContext.sportId]
  );

  useEffect(() => {
    setSampleVars((current) => ({
      ...current,
      copyStyle: previewContext.copyStyle,
      sportId: previewContext.sportId ?? '',
      variant: previewContext.variant ?? '',
      activityLabel: previewContext.activityLabel ?? '',
    }));
  }, [previewContext]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminFetch<MatchCopyGetResponse>(session, { action: 'match_copy:get' });
      const nextValue = normalizeMatchCopyOverrides(isObject(result.value) ? (result.value as Record<string, unknown>) : {});
      setBaseline(nextValue);
      setDraft(cloneValue(nextValue));
      setPreviewOverrides(cloneValue(nextValue));
      setHistory(Array.isArray(result.history) ? result.history : []);
      setMeta({
        updated_at: typeof result.settings?.updated_at === 'string' ? result.settings.updated_at : null,
        updated_by: typeof result.settings?.updated_by === 'string' ? result.settings.updated_by : null,
      });
      setSaveState('saved');
      setSaveError(null);
      setStatus(null);
    } catch (error) {
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to load match copy.' });
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  const persistDraft = useCallback(
    async (reason: 'autosave' | 'manual' | 'restore' = 'manual') => {
      const normalized = normalizeMatchCopyOverrides(draftRef.current);
      if (
        reason === 'autosave' &&
        stableStringify(normalized) === stableStringify(baselineRef.current)
      ) {
        setSaveState('saved');
        return true;
      }
      if (savePromiseRef.current) {
        pendingAutosaveRef.current = true;
        return savePromiseRef.current;
      }

      setSaveState('saving');
      setSaveError(null);
      const savePromise = adminFetch<MatchCopySaveResponse>(session, {
        action: 'match_copy:save',
        value: normalized,
        expectedUpdatedAt: metaRef.current.updated_at,
      })
        .then((result) => {
          const savedValue = normalizeMatchCopyOverrides(
            isObject(result.value) ? (result.value as Record<string, unknown>) : {},
          );
          const draftAtSaveStart = stableStringify(normalized);
          setBaseline(savedValue);
          setHistory(Array.isArray(result.history) ? result.history : []);
          setMeta({
            updated_at: typeof result.settings?.updated_at === 'string' ? result.settings.updated_at : null,
            updated_by: typeof result.settings?.updated_by === 'string' ? result.settings.updated_by : null,
          });
          setPreviewOverrides(cloneValue(savedValue));
          setDraft((current) => {
            const currentNormalized = normalizeMatchCopyOverrides(current);
            return stableStringify(currentNormalized) === draftAtSaveStart ? cloneValue(savedValue) : current;
          });
          setSaveState('saved');
          setSaveError(null);
          setStatus(null);
          return true;
        })
        .catch((error: Error) => {
          setSaveState('error');
          setSaveError(error.message || 'Failed to save match copy.');
          setStatus({ tone: 'error', text: error.message || 'Failed to save match copy.' });
          return false;
        })
        .finally(() => {
          savePromiseRef.current = null;
          if (pendingAutosaveRef.current) {
            pendingAutosaveRef.current = false;
            void persistDraft('autosave');
          }
        });

      savePromiseRef.current = savePromise;
      return savePromise;
    },
    [session],
  );

  useEffect(() => {
    if (loading || !dirty) return;
    const timer = window.setTimeout(() => {
      void persistDraft('autosave');
    }, 900);
    return () => window.clearTimeout(timer);
  }, [dirty, loading, draft, persistDraft]);

  const fieldsForCurrentStep = useMemo(() => fieldsForStep(step), [step]);
  const filteredFields = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sortFieldsForEditing(fieldsForCurrentStep).filter((field) => {
      const audienceMatch = audienceFilter === 'all' ? true : field.audience === audienceFilter;
      const intentMatch = intentFilter === 'all' ? true : fieldIntent(field) === intentFilter;
      const searchMatch = !query
        ? true
        : `${field.label} ${field.path}`.toLowerCase().includes(query);
      const modifiedMatch = !modifiedOnly
        ? true
        : textValue(getPathValue(draft, resolveScopedFieldPath(field.path, editorScope))).trim().length > 0;
      return audienceMatch && intentMatch && searchMatch && modifiedMatch;
    });
  }, [audienceFilter, draft, editorScope, fieldsForCurrentStep, intentFilter, modifiedOnly, search]);

  useEffect(() => {
    if (!filteredFields.length) {
      setSelectedFieldPath('');
      return;
    }
    if (!filteredFields.some((field) => field.path === selectedFieldPath)) {
      setSelectedFieldPath(filteredFields[0].path);
    }
  }, [filteredFields, selectedFieldPath]);

  const selectedField = useMemo(
    () => MATCH_COPY_FIELDS.find((field) => field.path === selectedFieldPath) ?? null,
    [selectedFieldPath],
  );
  const selectedScopedPath = useMemo(
    () => (selectedField ? resolveScopedFieldPath(selectedField.path, editorScope) : ''),
    [editorScope, selectedField]
  );
  const selectedOverrideValue = useMemo(
    () => (selectedScopedPath ? textValue(getPathValue(draft, selectedScopedPath)) : ''),
    [draft, selectedScopedPath]
  );
  const selectedInheritedValue = useMemo(() => {
    if (!selectedField) return '';
    return textValue(getContextualPathValue(effectiveDraft, selectedField.path, previewTarget === 'base' ? null : previewContext));
  }, [effectiveDraft, previewContext, previewTarget, selectedField]);
  const selectedFieldIndex = useMemo(
    () => filteredFields.findIndex((field) => field.path === selectedFieldPath),
    [filteredFields, selectedFieldPath],
  );
  const previousField = selectedFieldIndex > 0 ? filteredFields[selectedFieldIndex - 1] : null;
  const nextField =
    selectedFieldIndex >= 0 && selectedFieldIndex < filteredFields.length - 1
      ? filteredFields[selectedFieldIndex + 1]
      : null;

  const updateField = useCallback((field: MatchCopyField, nextValue: string) => {
    setSaveError(null);
    setDraft((current) => {
      const next = cloneValue(current);
      const scopedPath = resolveScopedFieldPath(field.path, editorScope);
      const defaultValue =
        editorScope.kind === 'base'
          ? textValue(getPathValue(MATCH_COPY_DEFAULTS, field.path))
          : '';
      if (!nextValue.trim().length || (editorScope.kind === 'base' && nextValue === defaultValue)) {
        deletePathValue(next, scopedPath);
      } else {
        setPathValue(next, scopedPath, nextValue);
      }
      return next;
    });
  }, [editorScope]);

  const resetField = useCallback((field: MatchCopyField) => {
    setSaveError(null);
    setDraft((current) => {
      const next = cloneValue(current);
      deletePathValue(next, resolveScopedFieldPath(field.path, editorScope));
      return next;
    });
  }, [editorScope]);

  const resetFields = useCallback((fields: MatchCopyField[]) => {
    if (!fields.length) return;
    setSaveError(null);
    setDraft((current) => {
      const next = cloneValue(current);
      fields.forEach((field) => deletePathValue(next, resolveScopedFieldPath(field.path, editorScope)));
      return next;
    });
  }, [editorScope]);

  const restoreHistory = useCallback(
    async (historyId: string) => {
      setSaveState('saving');
      try {
        const result = await adminFetch<MatchCopySaveResponse>(session, {
          action: 'match_copy:restore',
          historyId,
          expectedUpdatedAt: metaRef.current.updated_at,
        });
        const restoredValue = normalizeMatchCopyOverrides(
          isObject(result.value) ? (result.value as Record<string, unknown>) : {},
        );
        setBaseline(restoredValue);
        setDraft(cloneValue(restoredValue));
        setPreviewOverrides(cloneValue(restoredValue));
        setHistory(Array.isArray(result.history) ? result.history : []);
        setMeta({
          updated_at: typeof result.settings?.updated_at === 'string' ? result.settings.updated_at : null,
          updated_by: typeof result.settings?.updated_by === 'string' ? result.settings.updated_by : null,
        });
        setSaveState('saved');
        setSaveError(null);
        setStatus({ tone: 'success', text: 'History version restored.' });
      } catch (error) {
        setSaveState('error');
        setSaveError((error as Error).message || 'Failed to restore history.');
        setStatus({ tone: 'error', text: (error as Error).message || 'Failed to restore history.' });
      }
    },
    [session],
  );

  const saveStateLabel =
    saveState === 'saving' ? 'Saving…' : saveError ? 'Save failed' : dirty ? 'Unsaved' : 'Saved';
  const saveStateDetail =
    saveState === 'saving'
      ? 'Writing the current working draft…'
      : saveError
        ? 'The latest edits are still local.'
        : dirty
          ? 'Unsaved edits will publish after autosave.'
          : meta.updated_at
            ? `Last saved ${formatDate(meta.updated_at)}`
            : 'No saved revisions yet.';
  const previewStateDetail =
    saveState === 'saving'
      ? 'Saving changes…'
      : saveError
        ? 'Preview may be behind; retry.'
        : dirty
          ? 'Unsaved edits will appear after autosave.'
          : 'Saved draft is showing in preview.';

  const stepGroups = useMemo(
    () =>
      MATCH_COPY_AUDIENCES.map((audience) => {
        const fields = filteredFields.filter((field) => field.audience === audience.key);
        return { audience, fields };
      }).filter((group) => group.fields.length > 0),
    [filteredFields],
  );
  const quickFields = useMemo(() => {
    const priorityPool = sortFieldsForEditing(
      fieldsForCurrentStep.filter((field) => {
        if (audienceFilter !== 'all' && field.audience !== audienceFilter) return false;
        if (intentFilter !== 'all' && fieldIntent(field) !== intentFilter) return false;
        return true;
      }),
    );
    const preferred = priorityPool.filter((field) => {
      const intent = fieldIntent(field);
      return intent === 'titles' || intent === 'guidance' || intent === 'actions';
    });
    return (preferred.length ? preferred : priorityPool).slice(0, 6);
  }, [audienceFilter, fieldsForCurrentStep, intentFilter]);
  const selectedStepMeta = useMemo(
    () => MATCH_COPY_STEPS.find((item) => item.key === step) ?? MATCH_COPY_STEPS[0],
    [step],
  );
  const selectedAudienceMeta = selectedField ? AUDIENCE_META[selectedField.audience] : null;
  const selectedIntentMeta = selectedField ? INTENT_META.find((item) => item.key === fieldIntent(selectedField)) ?? null : null;
  const selectedFieldHasOverride = selectedField
    ? textValue(getPathValue(draft, resolveScopedFieldPath(selectedField.path, editorScope))).trim().length > 0
    : false;
  const relatedFields = useMemo(() => {
    if (!selectedField) return [];
    const sameAudience = sortFieldsForEditing(
      fieldsForCurrentStep.filter(
        (field) => field.path !== selectedField.path && field.audience === selectedField.audience,
      ),
    );
    const sameIntent = sameAudience.filter((field) => fieldIntent(field) === fieldIntent(selectedField));
    return (sameIntent.length ? sameIntent : sameAudience).slice(0, 5);
  }, [fieldsForCurrentStep, selectedField]);

  return (
    <div className="admin-page cms-workspace match-copy-workspace">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Match Experience Copy</div>
          <div className="admin-subtitle">Tune host, player, and shared copy with a save-safe authoring workspace.</div>
        </div>
        <div className="admin-actions">
          <button className="admin-btn" type="button" onClick={load} disabled={loading || saveState === 'saving'}>
            Reload
          </button>
          <button
            className="admin-btn"
            type="button"
            onClick={() => {
              setSaveError(null);
              setDraft({});
            }}
            disabled={loading || saveState === 'saving'}
          >
            Reset all
          </button>
          <button className="admin-btn primary" type="button" onClick={() => { void persistDraft('manual'); }} disabled={loading || saveState === 'saving'}>
            {saveState === 'saving' ? 'Saving…' : 'Save now'}
          </button>
        </div>
      </div>

      {status && shouldShowStatusBanner(status) ? statusPill(status) : null}

      <div className="cms-layout">
        <aside className="cms-editor-pane match-copy-editor-pane">
          <div className="cms-editor-pane-head">
            <div className="cms-editor-pane-meta">
              <div className="admin-section-title">Editor</div>
              <div className="admin-muted">Draft key <span className="admin-mono">{MATCH_COPY_KEY}</span></div>
              <div className="cms-save-state-line">
                <span className={clsx('cms-save-pill', `is-${saveState}`)}>{saveStateLabel}</span>
                <span className="cms-save-state-detail">{saveStateDetail}</span>
              </div>
            </div>
            <div className="cms-stage-toolbar-group">
              <button className={clsx('admin-btn', editorMode === 'content' && 'primary')} type="button" onClick={() => setEditorMode('content')}>Content</button>
              <button className={clsx('admin-btn', editorMode === 'preview' && 'primary')} type="button" onClick={() => setEditorMode('preview')}>Preview settings</button>
              <button className={clsx('admin-btn', editorMode === 'history' && 'primary')} type="button" onClick={() => setEditorMode('history')}>History</button>
            </div>
          </div>

          <div className="cms-page-context cms-soft-card">
            <div className="cms-page-context-head">
              <div>
                <div className="cms-editor-form-kicker">Current surface</div>
                <h3 className="cms-page-context-title">Match Experience Copy</h3>
              </div>
              <div className="cms-page-context-route">{config.hubActiveEnv}</div>
            </div>
            <div className="cms-page-context-meta">
              <span className="admin-badge">{countOverridesForFields(draft, MATCH_COPY_FIELDS, editorScope)} overrides</span>
              <span className="admin-badge">Updated by {shortUserLabel(meta.updated_by)}</span>
              <span className="admin-badge">{formatDate(meta.updated_at)}</span>
            </div>
            <div className="cms-editor-form-section" style={{ marginTop: 12 }}>
              <div className="cms-editor-form-section-head">
                <div className="cms-editor-form-section-kicker">Editing scope</div>
                <h4>{scopeSummary(editorScope)}</h4>
              </div>
              <div className="cms-page-context-actions" style={{ marginBottom: 10 }}>
                <button className={clsx('admin-btn small', editorScope.kind === 'base' && 'primary')} type="button" onClick={() => setEditorScope({ kind: 'base' })}>Base</button>
                <button className={clsx('admin-btn small', editorScope.kind === 'style' && 'primary')} type="button" onClick={() => setEditorScope({ kind: 'style', copyStyle: previewContext.copyStyle })}>Family</button>
                <button className={clsx('admin-btn small', editorScope.kind === 'sport' && 'primary')} type="button" onClick={() => setEditorScope({ kind: 'sport', sportId: previewContext.sportId ?? 'pickleball' })}>Sport</button>
                <button className={clsx('admin-btn small', editorScope.kind === 'variant' && 'primary')} type="button" onClick={() => setEditorScope({ kind: 'variant', sportId: previewContext.sportId ?? 'weightlifting', variant: previewContext.variant ?? 'one_rep_max' })}>Variant</button>
              </div>
              {editorScope.kind === 'style' ? (
                <label className="admin-label">
                  Family
                  <select
                    className="admin-input"
                    value={editorScope.copyStyle}
                    onChange={(event) => setEditorScope({ kind: 'style', copyStyle: event.target.value as any })}
                  >
                    {MATCH_COPY_STYLE_EDITOR_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              ) : null}
              {editorScope.kind === 'sport' || editorScope.kind === 'variant' ? (
                <div className="match-copy-settings-grid">
                  <label className="admin-label">
                    Sport
                    <select
                      className="admin-input"
                      value={editorScope.sportId}
                      onChange={(event) => {
                        const sportId = event.target.value;
                        if (editorScope.kind === 'sport') {
                          setEditorScope({ kind: 'sport', sportId });
                          return;
                        }
                        const variants = MATCH_COPY_VARIANT_EDITOR_OPTIONS[sportId] ?? [];
                        setEditorScope({
                          kind: 'variant',
                          sportId,
                          variant: variants[0]?.value ?? 'high_score',
                        });
                      }}
                    >
                      {MATCH_COPY_SPORT_EDITOR_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  {editorScope.kind === 'variant' ? (
                    <label className="admin-label">
                      Variant
                      <select
                        className="admin-input"
                        value={editorScope.variant}
                        onChange={(event) => setEditorScope({ kind: 'variant', sportId: editorScope.sportId, variant: event.target.value })}
                      >
                        {editorVariantOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="cms-page-context-actions">
              <button className="admin-btn" type="button" onClick={() => resetFields(fieldsForCurrentStep)}>
                Reset step
              </button>
              <button
                className="admin-btn"
                type="button"
                onClick={() => {
                  if (audienceFilter === 'all') return;
                  resetFields(fieldsForAudience(step, audienceFilter));
                }}
                disabled={audienceFilter === 'all'}
              >
                Reset group
              </button>
            </div>
          </div>

          {editorMode === 'content' ? (
            <div className="cms-editor-stack match-copy-editor-stack">
              <div className="cms-card-stack cms-soft-card cms-section-column match-copy-section-column">
                <div className="cms-pane-head">
                  <div>
                    <div className="admin-section-title">Step flow</div>
                    <div className="admin-muted">Choose the match moment you want to edit.</div>
                  </div>
                </div>
                <div className="match-copy-step-grid">
                  {MATCH_COPY_STEPS.map((item) => (
                    <button
                      key={item.key}
                      className={clsx('match-copy-step-card', step === item.key && 'active')}
                      type="button"
                      onClick={() => {
                        setStep(item.key);
                        setSearch('');
                        setIntentFilter('all');
                        setAudienceFilter('all');
                      }}
                    >
                      <div className="match-copy-step-card-top">
                        <span>{item.number ?? '•'}</span>
                        <strong>{item.label}</strong>
                      </div>
                      <div className="match-copy-step-card-meta">
                        {countOverridesForFields(draft, fieldsForStep(item.key), editorScope)} overrides
                      </div>
                    </button>
                  ))}
                </div>

                <div className="cms-editor-form-section match-copy-quick-section">
                  <div className="cms-editor-form-section-head">
                    <div className="cms-editor-form-section-kicker">Quick edits</div>
                    <h4>Most likely fields for {selectedStepMeta.label}</h4>
                  </div>
                  <div className="match-copy-quick-grid">
                    {quickFields.map((field) => {
                      const hasOverride = textValue(getPathValue(draft, resolveScopedFieldPath(field.path, editorScope))).trim().length > 0;
                      return (
                        <button
                          key={field.path}
                          className={clsx('match-copy-quick-card', selectedFieldPath === field.path && 'active')}
                          type="button"
                          onClick={() => setSelectedFieldPath(field.path)}
                        >
                          <div className="match-copy-quick-card-top">
                            <span className="admin-badge">{AUDIENCE_META[field.audience].title}</span>
                            {hasOverride ? <span className="admin-badge override">Override</span> : null}
                          </div>
                          <strong>{summarizeField(field)}</strong>
                          <span>{field.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="match-copy-audience-grid">
                  {MATCH_COPY_AUDIENCES.map((audience) => {
                    const audienceFields = fieldsForAudience(step, audience.key);
                    const isActive = audienceFilter === audience.key;
                    const audienceVisibleFields = fieldsForCurrentStep.filter((field) => {
                      if (field.audience !== audience.key) return false;
                      const query = search.trim().toLowerCase();
                      const searchMatch = !query
                        ? true
                        : `${field.label} ${field.path}`.toLowerCase().includes(query);
                      const modifiedMatch = !modifiedOnly
                        ? true
                        : textValue(getPathValue(draft, resolveScopedFieldPath(field.path, editorScope))).trim().length > 0;
                      return searchMatch && modifiedMatch;
                    });
                    return (
                      <button
                        key={audience.key}
                        className={clsx('match-copy-audience-card', isActive && 'active')}
                        type="button"
                        onClick={() => {
                          const nextFilter = isActive ? 'all' : audience.key;
                          setAudienceFilter(nextFilter);
                          setIntentFilter('all');
                          const nextField =
                            nextFilter === 'all'
                              ? filteredFields[0]
                              : audienceVisibleFields[0];
                          if (nextField) setSelectedFieldPath(nextField.path);
                        }}
                      >
                        <div className="match-copy-audience-card-head">
                          <strong>{AUDIENCE_META[audience.key].title}</strong>
                          <span>{countOverridesForFields(draft, audienceFields, editorScope)} overrides</span>
                        </div>
                        <p>{AUDIENCE_META[audience.key].description}</p>
                      </button>
                    );
                  })}
                </div>

                <div className="match-copy-filter-bar">
                  <label className="admin-label">
                    Search fields
                    <input
                      className="admin-input"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Find player guidance, CTA, subtitle…"
                    />
                  </label>
                  <div className="match-copy-filter-actions">
                    <button
                      className={clsx('admin-btn small', intentFilter === 'all' && 'primary')}
                      type="button"
                      onClick={() => setIntentFilter('all')}
                    >
                      All field types
                    </button>
                    {INTENT_META.map((intent) => (
                      <button
                        key={intent.key}
                        className={clsx('admin-btn small', intentFilter === intent.key && 'primary')}
                        type="button"
                        onClick={() => setIntentFilter(intent.key)}
                      >
                        {intent.label}
                      </button>
                    ))}
                    <button
                      className={clsx('admin-btn small', modifiedOnly && 'primary')}
                      type="button"
                      onClick={() => setModifiedOnly((current) => !current)}
                    >
                      Modified only
                    </button>
                  </div>
                </div>

                <div className="match-copy-field-groups">
                  {stepGroups.map((group) => (
                    <div className="match-copy-field-group" key={group.audience.key}>
                      <div className="match-copy-field-group-head">
                        <strong>{group.audience.label}</strong>
                        <span>{countOverridesForFields(draft, group.fields, editorScope)} overrides</span>
                      </div>
                      <div className="match-copy-field-list">
                      {group.fields.map((field) => {
                          const hasOverride = textValue(getPathValue(draft, resolveScopedFieldPath(field.path, editorScope))).trim().length > 0;
                          return (
                            <button
                              key={field.path}
                              className={clsx('match-copy-field-list-item', selectedFieldPath === field.path && 'active')}
                              type="button"
                              onClick={() => setSelectedFieldPath(field.path)}
                            >
                              <div>
                                <strong>{field.label}</strong>
                                <span>{field.path}</span>
                              </div>
                              <div className="match-copy-field-list-item-meta">
                                <span className="admin-badge subtle">{INTENT_META.find((item) => item.key === fieldIntent(field))?.label ?? 'Field'}</span>
                                {hasOverride ? <span className="admin-badge override">Override</span> : <span className="admin-badge">Default</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {!stepGroups.length ? (
                    <div className="cms-soft-card cms-empty-editor">
                      <strong>No fields match this filter</strong>
                      <span>Try another audience or search query.</span>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="cms-soft-card cms-card-stack cms-editor-column match-copy-detail-column">
                {selectedField ? (
                  <>
                    <div className="cms-editor-form-head">
                      <div>
                        <div className="cms-editor-form-kicker">{selectedField.audience}</div>
                        <h3 className="cms-editor-form-title">{selectedField.label}</h3>
                        <div className="match-copy-detail-meta">
                          <span className="admin-badge">{selectedStepMeta.label}</span>
                          <span className="admin-badge">{selectedAudienceMeta?.title}</span>
                          {selectedIntentMeta ? <span className="admin-badge">{selectedIntentMeta.label}</span> : null}
                          <span className={clsx('admin-badge', selectedFieldHasOverride && 'override')}>
                            {selectedFieldHasOverride ? 'Override active' : 'Using default'}
                          </span>
                        </div>
                        <p className="match-copy-detail-description">
                          {selectedAudienceMeta?.description}
                          {' '}
                          {selectedField.help ?? selectedAudienceMeta?.emphasis}
                        </p>
                      </div>
                      <div className="cms-page-context-actions">
                        {previousField ? (
                          <button className="admin-btn small ghost" type="button" onClick={() => setSelectedFieldPath(previousField.path)}>
                            Previous
                          </button>
                        ) : null}
                        {nextField ? (
                          <button className="admin-btn small ghost" type="button" onClick={() => setSelectedFieldPath(nextField.path)}>
                            Next
                          </button>
                        ) : null}
                        <button
                          className="admin-btn small"
                          type="button"
                          onClick={async () => {
                            await navigator.clipboard.writeText(
                              formatTemplate(
                                textValue(getContextualPathValue(effectiveDraft, selectedField.path, previewTarget === 'base' ? null : previewContext)),
                                sampleVars,
                              ),
                            );
                            setStatus({ tone: 'success', text: 'Effective text copied.' });
                          }}
                        >
                          Copy effective text
                        </button>
                        <button className="admin-btn small" type="button" onClick={() => resetField(selectedField)}>
                          Reset to default
                        </button>
                      </div>
                    </div>

                    <div className="cms-editor-form-section">
                      <div className="cms-editor-form-section-head">
                        <div className="cms-editor-form-section-kicker">Core content</div>
                        <h4>Override</h4>
                      </div>
                      {selectedField.kind === 'rich_text' ? (
                        <MatchCopyRichEditor
                          value={selectedOverrideValue}
                          placeholder={selectedInheritedValue || textValue(getPathValue(MATCH_COPY_DEFAULTS, selectedField.path))}
                          onChange={(next) => updateField(selectedField, next)}
                        />
                      ) : (
                        <label className="admin-label">
                          Value
                          <textarea
                            className="admin-input match-copy-body-input"
                            rows={5}
                            value={selectedOverrideValue}
                            placeholder={selectedInheritedValue || textValue(getPathValue(MATCH_COPY_DEFAULTS, selectedField.path))}
                            onChange={(event) => updateField(selectedField, event.target.value)}
                          />
                        </label>
                      )}
                      {selectedField.help ? <div className="admin-help">{selectedField.help}</div> : null}
                      {selectedField.vars?.length ? (
                        <div className="admin-help">Available tokens: {selectedField.vars.map((variable) => <span className="admin-mono" key={variable}>{`{${variable}} `}</span>)}</div>
                      ) : null}
                      <div className="match-copy-surface-note">
                        <strong>Editing lens</strong>
                        <span>{selectedAudienceMeta?.emphasis}</span>
                      </div>
                      {relatedFields.length ? (
                        <div className="match-copy-related-strip">
                          <div className="match-copy-related-strip-head">
                            <strong>Continue with</strong>
                            <span>{selectedAudienceMeta?.title}</span>
                          </div>
                          <div className="match-copy-related-grid">
                            {relatedFields.map((field) => (
                              <button
                                key={field.path}
                                className="match-copy-related-chip"
                                type="button"
                                onClick={() => setSelectedFieldPath(field.path)}
                              >
                                {summarizeField(field)}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="cms-editor-form-section">
                      <div className="cms-editor-form-section-head">
                        <div className="cms-editor-form-section-kicker">Comparison</div>
                        <h4>Default vs effective output</h4>
                      </div>
                      <div className="match-copy-compare-grid">
                        <div className="match-copy-compare-card">
                          <div className="match-copy-compare-title">Inherited default</div>
                          <div className="match-copy-compare-body">
                            {selectedField.kind === 'rich_text' ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: formatTemplate(
                                    selectedInheritedValue || textValue(getPathValue(MATCH_COPY_DEFAULTS, selectedField.path)),
                                    sampleVars,
                                  ),
                                }}
                              />
                            ) : (
                              formatTemplate(selectedInheritedValue || textValue(getPathValue(MATCH_COPY_DEFAULTS, selectedField.path)), sampleVars)
                            )}
                          </div>
                        </div>
                        <div className="match-copy-compare-card accent">
                          <div className="match-copy-compare-title">Effective output</div>
                          <div className="match-copy-compare-body">
                            {selectedField.kind === 'rich_text' ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: formatTemplate(
                                    textValue(getContextualPathValue(effectiveDraft, selectedField.path, previewTarget === 'base' ? null : previewContext)),
                                    sampleVars,
                                  ),
                                }}
                              />
                            ) : (
                              formatTemplate(
                                textValue(getContextualPathValue(effectiveDraft, selectedField.path, previewTarget === 'base' ? null : previewContext)),
                                sampleVars,
                              )
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="admin-help">
                        Raw path: <span className="admin-mono">{selectedScopedPath || selectedField.path}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="cms-soft-card cms-empty-editor">
                    <strong>Select a field</strong>
                    <span>Choose a field from the navigator to edit its copy and compare it to the default.</span>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {editorMode === 'preview' ? (
            <div className="cms-soft-card cms-card-stack cms-editor-details">
              <div className="cms-pane-head">
                <div>
                  <div className="admin-section-title">Preview settings</div>
                  <div className="admin-muted">Choose a role, scenario, and sample values for tokenized copy.</div>
                </div>
              </div>
              <div className="match-copy-settings-grid">
                <div className="cms-editor-form-section">
                  <div className="cms-editor-form-section-head">
                    <div className="cms-editor-form-section-kicker">Viewport</div>
                    <h4>Frame</h4>
                  </div>
                  <div className="cms-page-context-actions">
                    <button className={clsx('admin-btn', previewViewport === 'phone' && 'primary')} type="button" onClick={() => setPreviewViewport('phone')}>Phone</button>
                    <button className={clsx('admin-btn', previewViewport === 'tablet' && 'primary')} type="button" onClick={() => setPreviewViewport('tablet')}>Tablet</button>
                    <button className={clsx('admin-btn', previewViewport === 'desktop' && 'primary')} type="button" onClick={() => setPreviewViewport('desktop')}>Desktop</button>
                  </div>
                </div>
                <div className="cms-editor-form-section">
                  <div className="cms-editor-form-section-head">
                    <div className="cms-editor-form-section-kicker">Role</div>
                    <h4>Perspective</h4>
                  </div>
                  <div className="cms-page-context-actions">
                    <button className={clsx('admin-btn', previewRole === 'host' && 'primary')} type="button" onClick={() => setPreviewRole('host')}>Host</button>
                    <button className={clsx('admin-btn', previewRole === 'player' && 'primary')} type="button" onClick={() => setPreviewRole('player')}>Player</button>
                  </div>
                </div>
              </div>

              <div className="cms-editor-form-section">
                <div className="cms-editor-form-section-head">
                  <div className="cms-editor-form-section-kicker">Context</div>
                  <h4>Preview override layer</h4>
                </div>
                <div className="cms-page-context-actions" style={{ marginBottom: 10 }}>
                  <button className={clsx('admin-btn small', previewTarget === 'base' && 'primary')} type="button" onClick={() => setPreviewTarget('base')}>Base</button>
                  <button className={clsx('admin-btn small', previewTarget === 'style' && 'primary')} type="button" onClick={() => setPreviewTarget('style')}>Family</button>
                  <button className={clsx('admin-btn small', previewTarget === 'sport' && 'primary')} type="button" onClick={() => setPreviewTarget('sport')}>Sport</button>
                  <button className={clsx('admin-btn small', previewTarget === 'variant' && 'primary')} type="button" onClick={() => setPreviewTarget('variant')}>Variant</button>
                </div>
                <div className="match-copy-settings-grid">
                  <label className="admin-label">
                    Family
                    <select
                      className="admin-input"
                      value={previewContext.copyStyle}
                      onChange={(event) =>
                        setPreviewContext((current) => ({
                          ...current,
                          copyStyle: event.target.value as any,
                        }))
                      }
                    >
                      {MATCH_COPY_STYLE_EDITOR_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-label">
                    Sport
                    <select
                      className="admin-input"
                      value={previewContext.sportId ?? ''}
                      onChange={(event) => {
                        const sportId = event.target.value;
                        const variants = MATCH_COPY_VARIANT_EDITOR_OPTIONS[sportId] ?? [];
                        setPreviewContext((current) => ({
                          ...current,
                          sportId,
                          variant: previewTarget === 'variant' ? variants[0]?.value ?? null : current.variant,
                        }));
                      }}
                    >
                      {MATCH_COPY_SPORT_EDITOR_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-label">
                    Variant
                    <select
                      className="admin-input"
                      value={previewContext.variant ?? ''}
                      onChange={(event) =>
                        setPreviewContext((current) => ({
                          ...current,
                          variant: event.target.value || null,
                        }))
                      }
                    >
                      <option value="">None</option>
                      {previewVariantOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-label">
                    Activity
                    <input
                      className="admin-input"
                      value={previewContext.activityLabel ?? ''}
                      onChange={(event) =>
                        setPreviewContext((current) => ({
                          ...current,
                          activityLabel: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>

              <div className="cms-editor-form-section">
                <div className="cms-editor-form-section-head">
                  <div className="cms-editor-form-section-kicker">Sample variables</div>
                  <h4>Token substitution</h4>
                </div>
                <div className="match-copy-settings-grid">
                  <label className="admin-label">
                    Host
                    <input className="admin-input" value={String(sampleVars.host)} onChange={(event) => setSampleVars((current) => ({ ...current, host: event.target.value }))} />
                  </label>
                  <label className="admin-label">
                    Controller
                    <input className="admin-input" value={String(sampleVars.controller)} onChange={(event) => setSampleVars((current) => ({ ...current, controller: event.target.value }))} />
                  </label>
                  <label className="admin-label">
                    Win points
                    <input className="admin-input" value={String(sampleVars.winPoints)} onChange={(event) => setSampleVars((current) => ({ ...current, winPoints: Number(event.target.value) || 0 }))} />
                  </label>
                  <label className="admin-label">
                    Activity label
                    <input className="admin-input" value={String(sampleVars.activityLabel)} onChange={(event) => setSampleVars((current) => ({ ...current, activityLabel: event.target.value }))} />
                  </label>
                </div>
              </div>
            </div>
          ) : null}

          {editorMode === 'history' ? (
            <div className="cms-soft-card cms-card-stack cms-editor-details">
              <div className="cms-pane-head">
                <div>
                  <div className="admin-section-title">History</div>
                  <div className="admin-muted">Recent saved versions for match experience copy.</div>
                </div>
              </div>
              <div className="cms-revision-list">
                {history.map((item) => (
                  <div className="cms-revision-item" key={item.id}>
                    <div>
                      <strong>{item.source === 'restore' ? 'Restored version' : 'Saved version'}</strong>
                      <div className="admin-muted">{formatDate(item.created_at)} · {shortUserLabel(item.created_by)}</div>
                    </div>
                    <button className="admin-btn small ghost" type="button" onClick={() => { void restoreHistory(item.id); }}>
                      Restore
                    </button>
                  </div>
                ))}
              </div>
              <details className="cms-advanced-details">
                <summary>Advanced → Raw JSON</summary>
                <textarea className="admin-input cms-json-textarea" rows={14} value={stableStringify(draft)} readOnly />
              </details>
            </div>
          ) : null}
        </aside>

        <MatchCopyPreview
          step={step}
          scenario={currentScenario}
          role={previewRole}
          viewport={previewViewport}
          effective={effectivePreview}
          previewStateDetail={previewStateDetail}
          setRole={setPreviewRole}
          setViewport={setPreviewViewport}
          setScenario={(next) => setScenarioByStep((current) => ({ ...current, [step]: next }))}
        />
      </div>
    </div>
  );
}
